import { scopeProjectRef, scopeThreadRef } from "@threadlines/client-runtime";
import type { EnvironmentId, VcsRef, ThreadId } from "@threadlines/contracts";
import { resolveThreadWorkingCwd } from "@threadlines/shared/threadCwd";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { LegendList, type LegendListRef } from "@legendapp/list/react";
import { ChevronDownIcon } from "lucide-react";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useState,
  useTransition,
} from "react";

import { useComposerDraftStore, type DraftId } from "../composerDraftStore";
import { readEnvironmentApi } from "../environmentApi";
import { gitBranchSearchInfiniteQueryOptions, gitQueryKeys } from "../lib/gitReactQuery";
import { useGitStatus } from "../lib/gitStatusState";
import { cn } from "../lib/utils";
import { parsePullRequestReference } from "../pullRequestReference";
import { getSourceControlPresentation } from "../sourceControlPresentation";
import { getVcsRefBadge } from "../worktreeCleanup";
import { useStore } from "../store";
import { createProjectSelectorByRef, createThreadSelectorByRef } from "../storeSelectors";
import { useThreadCheckoutSelection } from "../hooks/useThreadCheckoutSelection";
import {
  annotateMissingCheckoutLabel,
  deriveLocalBranchNameFromRemoteRef,
  hasActiveThreadTurn,
  queuedCheckoutSwitchToast,
  resolveActiveWorktreePath,
  resolveBranchSelectionTarget,
  resolveBranchToolbarValue,
  resolveCheckoutPickerRefsCwd,
  resolveDefaultWorktreeBaseBranch,
  resolveDraftEnvModeAfterBranchChange,
  resolveEffectiveEnvMode,
  shouldIncludeBranchPickerItem,
} from "./BranchToolbar.logic";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxListVirtualized,
  ComboboxPopup,
  ComboboxStatus,
  ComboboxTrigger,
} from "./ui/combobox";
import { inlinePickerTriggerClassName } from "./ui/select";
import { stackedThreadToast, toastManager } from "./ui/toast";

interface BranchToolbarBranchSelectorProps {
  className?: string;
  /**
   * `sentence` sets the picker in running words: the branch name alone as
   * underlined text, with the words around it saying whether the thread starts
   * from it or works on it.
   */
  presentation?: "chip" | "sentence";
  environmentId: EnvironmentId;
  threadId: ThreadId;
  draftId?: DraftId;
  envLocked: boolean;
  effectiveEnvModeOverride?: "local" | "worktree";
  activeThreadBranchOverride?: string | null;
  onActiveThreadBranchOverrideChange?: (refName: string | null) => void;
  onCheckoutPullRequestRequest?: (reference: string) => void;
  onComposerFocusRequest?: () => void;
}

/** A branch action that would rewrite files in the checkout an agent is
 *  currently working in, held back until the user confirms. */
type PendingWorkingTreeAction =
  | { readonly kind: "switch"; readonly refName: VcsRef }
  | { readonly kind: "create"; readonly name: string };

function toBranchActionErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "An error occurred.";
}

function getBranchTriggerLabel(input: {
  activeWorktreePath: string | null;
  effectiveEnvMode: "local" | "worktree";
  resolvedActiveBranch: string | null;
}): string {
  const { activeWorktreePath, effectiveEnvMode, resolvedActiveBranch } = input;
  if (!resolvedActiveBranch) {
    return "Select ref";
  }
  if (effectiveEnvMode === "worktree" && !activeWorktreePath) {
    return `From ${resolvedActiveBranch}`;
  }
  return resolvedActiveBranch;
}

export function BranchToolbarBranchSelector({
  className,
  presentation = "chip",
  environmentId,
  threadId,
  draftId,
  envLocked,
  effectiveEnvModeOverride,
  activeThreadBranchOverride,
  onActiveThreadBranchOverrideChange,
  onCheckoutPullRequestRequest,
  onComposerFocusRequest,
}: BranchToolbarBranchSelectorProps) {
  // ---------------------------------------------------------------------------
  // Thread / project state (pushed down from parent to colocate with mutation)
  // ---------------------------------------------------------------------------
  const threadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const serverThreadSelector = useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]);
  const serverThread = useStore(serverThreadSelector);
  const serverSession = serverThread?.session ?? null;
  const draftThread = useComposerDraftStore((store) =>
    draftId ? store.getDraftSession(draftId) : store.getDraftThreadByRef(threadRef),
  );
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);

  const activeProjectRef = serverThread
    ? scopeProjectRef(serverThread.environmentId, serverThread.projectId)
    : draftThread
      ? scopeProjectRef(draftThread.environmentId, draftThread.projectId)
      : null;
  const activeProjectSelector = useMemo(
    () => createProjectSelectorByRef(activeProjectRef),
    [activeProjectRef],
  );
  const activeProject = useStore(activeProjectSelector);

  const activeThreadId = serverThread?.id ?? (draftThread ? threadId : undefined);
  const activeThreadBranch =
    activeThreadBranchOverride !== undefined
      ? activeThreadBranchOverride
      : (serverThread?.branch ?? draftThread?.branch ?? null);
  const activeProjectCwd = activeProject?.cwd ?? null;
  const storedWorktreePath = serverThread?.worktreePath ?? draftThread?.worktreePath ?? null;
  const activeWorktreePath = resolveActiveWorktreePath(activeProjectCwd, storedWorktreePath);
  const branchCwd = activeProjectCwd
    ? resolveThreadWorkingCwd({
        projectCwd: activeProjectCwd,
        worktreePath: activeWorktreePath,
        effectiveCwd: serverThread?.effectiveCwd ?? null,
      })
    : activeWorktreePath;
  const hasServerThread = serverThread !== undefined;
  const effectiveEnvMode =
    effectiveEnvModeOverride ??
    resolveEffectiveEnvMode({
      activeWorktreePath,
      hasServerThread,
      draftThreadEnvMode: draftThread?.envMode,
    });

  const selectServerThreadCheckout = useThreadCheckoutSelection({
    threadRef: serverThread ? threadRef : null,
    thread: serverThread ?? null,
    onBranchOverrideChange: onActiveThreadBranchOverrideChange,
  });

  // ---------------------------------------------------------------------------
  // Thread branch mutation (colocated — only this component calls it)
  // ---------------------------------------------------------------------------
  const setThreadBranch = useCallback(
    (branch: string | null, worktreePath: string | null) => {
      if (!activeThreadId || !activeProject) return;
      if (hasServerThread) {
        selectServerThreadCheckout(branch, worktreePath);
        return;
      }
      const nextDraftEnvMode = resolveDraftEnvModeAfterBranchChange({
        nextWorktreePath: worktreePath,
        currentWorktreePath: activeWorktreePath,
        effectiveEnvMode,
      });
      setDraftThreadContext(draftId ?? threadRef, {
        branch,
        worktreePath,
        envMode: nextDraftEnvMode,
        projectRef: scopeProjectRef(environmentId, activeProject.id),
      });
    },
    [
      activeThreadId,
      activeProject,
      activeWorktreePath,
      hasServerThread,
      selectServerThreadCheckout,
      setDraftThreadContext,
      draftId,
      threadRef,
      environmentId,
      effectiveEnvMode,
    ],
  );

  // ---------------------------------------------------------------------------
  // Git ref queries
  // ---------------------------------------------------------------------------
  const queryClient = useQueryClient();
  const [pendingWorkingTreeAction, setPendingWorkingTreeAction] =
    useState<PendingWorkingTreeAction | null>(null);
  const [isBranchMenuOpen, setIsBranchMenuOpen] = useState(false);
  const [branchQuery, setBranchQuery] = useState("");
  const deferredBranchQuery = useDeferredValue(branchQuery);

  const branchStatusQuery = useGitStatus({ environmentId, cwd: branchCwd });
  // A checkout that was deleted lists no refs at all, which would leave the
  // picker open and empty. Refs then come from the project root so the valid
  // alternatives are still there to switch to.
  const selectedCheckoutMissing = branchStatusQuery.data?.pathMissing === true;
  const refsCwd = resolveCheckoutPickerRefsCwd({
    selectedCwd: branchCwd,
    projectCwd: activeProjectCwd,
    selectedCheckoutMissing,
  });
  const trimmedBranchQuery = branchQuery.trim();
  const deferredTrimmedBranchQuery = deferredBranchQuery.trim();

  useEffect(() => {
    if (!refsCwd) return;
    void queryClient.prefetchInfiniteQuery(
      gitBranchSearchInfiniteQueryOptions({ environmentId, cwd: refsCwd, query: "" }),
    );
  }, [refsCwd, environmentId, queryClient]);

  const {
    data: branchesSearchData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isBranchesSearchPending,
  } = useInfiniteQuery(
    gitBranchSearchInfiniteQueryOptions({
      environmentId,
      cwd: refsCwd,
      query: deferredTrimmedBranchQuery,
    }),
  );
  const refs = useMemo(
    () => branchesSearchData?.pages.flatMap((page) => page.refs) ?? [],
    [branchesSearchData?.pages],
  );
  // With the checkout deleted, `refs` list the project root's branches, whose
  // current branch is the root's, not this thread's. Skipping that fallback
  // lets the label resolve from the thread's own recorded branch instead.
  const currentGitBranch =
    branchStatusQuery.data?.refName ??
    (selectedCheckoutMissing ? null : (refs.find((refName) => refName.current)?.name ?? null));
  const sourceControlPresentation = useMemo(
    () => getSourceControlPresentation(branchStatusQuery.data?.sourceControlProvider),
    [branchStatusQuery.data?.sourceControlProvider],
  );
  const SourceControlIcon = sourceControlPresentation.Icon;
  const canonicalActiveBranch = resolveBranchToolbarValue({
    envMode: effectiveEnvMode,
    activeWorktreePath,
    activeThreadBranch,
    currentGitBranch,
  });
  const branchNames = useMemo(() => refs.map((refName) => refName.name), [refs]);
  const branchByName = useMemo(
    () => new Map(refs.map((refName) => [refName.name, refName] as const)),
    [refs],
  );
  const normalizedDeferredBranchQuery = deferredTrimmedBranchQuery.toLowerCase();
  const prReference = parsePullRequestReference(trimmedBranchQuery);
  const isSelectingWorktreeBase =
    effectiveEnvMode === "worktree" && !envLocked && !activeWorktreePath;
  const checkoutPullRequestItemValue =
    prReference && onCheckoutPullRequestRequest ? `__checkout_pull_request__:${prReference}` : null;
  const canCreateBranch = !isSelectingWorktreeBase && trimmedBranchQuery.length > 0;
  const hasExactBranchMatch = branchByName.has(trimmedBranchQuery);
  const createBranchItemValue = canCreateBranch
    ? `__create_new_branch__:${trimmedBranchQuery}`
    : null;
  const branchPickerItems = useMemo(() => {
    const items = [...branchNames];
    if (createBranchItemValue && !hasExactBranchMatch) {
      items.push(createBranchItemValue);
    }
    if (checkoutPullRequestItemValue) {
      items.unshift(checkoutPullRequestItemValue);
    }
    return items;
  }, [branchNames, checkoutPullRequestItemValue, createBranchItemValue, hasExactBranchMatch]);
  const filteredBranchPickerItems = useMemo(
    () =>
      normalizedDeferredBranchQuery.length === 0
        ? branchPickerItems
        : branchPickerItems.filter((itemValue) =>
            shouldIncludeBranchPickerItem({
              itemValue,
              normalizedQuery: normalizedDeferredBranchQuery,
              createBranchItemValue,
              checkoutPullRequestItemValue,
            }),
          ),
    [
      branchPickerItems,
      checkoutPullRequestItemValue,
      createBranchItemValue,
      normalizedDeferredBranchQuery,
    ],
  );
  const [resolvedActiveBranch, setOptimisticBranch] = useOptimistic(
    canonicalActiveBranch,
    (_currentBranch: string | null, optimisticBranch: string | null) => optimisticBranch,
  );
  const [isBranchActionPending, startBranchActionTransition] = useTransition();
  const shouldVirtualizeBranchList = filteredBranchPickerItems.length > 40;
  const totalBranchCount = branchesSearchData?.pages[0]?.totalCount ?? 0;
  const branchStatusText = isBranchesSearchPending
    ? "Loading refs..."
    : isFetchingNextPage
      ? "Loading more refs..."
      : hasNextPage
        ? `Showing ${refs.length} of ${totalBranchCount} refs`
        : null;

  // ---------------------------------------------------------------------------
  // Branch actions
  // ---------------------------------------------------------------------------
  const runBranchAction = (action: () => Promise<void>) => {
    startBranchActionTransition(async () => {
      await action().catch(() => undefined);
      await queryClient
        .invalidateQueries({ queryKey: gitQueryKeys.refs(environmentId, branchCwd) })
        .catch(() => undefined);
    });
  };

  // A pick that leaves the live session in a different checkout is queued, not
  // applied; the chip near the composer is easy to miss, so say it out loud.
  const announceQueuedCheckoutSwitch = (nextWorktreePath: string | null) => {
    const queued = queuedCheckoutSwitchToast({
      session: serverSession,
      activeProjectCwd,
      nextWorktreePath,
    });
    if (queued) {
      toastManager.add(stackedThreadToast({ type: "info", ...queued }));
    }
  };

  const runSwitchRef = (refName: VcsRef, checkoutCwd: string, nextWorktreePath: string | null) => {
    const api = readEnvironmentApi(environmentId);
    if (!api) return;
    const selectedBranchName = refName.isRemote
      ? deriveLocalBranchNameFromRemoteRef(refName.name)
      : refName.name;

    runBranchAction(async () => {
      const previousBranch = resolvedActiveBranch;
      setOptimisticBranch(selectedBranchName);
      try {
        const checkoutResult = await api.vcs.switchRef({
          cwd: checkoutCwd,
          refName: refName.name,
        });
        const nextBranchName = refName.isRemote
          ? (checkoutResult.refName ?? selectedBranchName)
          : selectedBranchName;
        setOptimisticBranch(nextBranchName);
        setThreadBranch(nextBranchName, nextWorktreePath);
        announceQueuedCheckoutSwitch(nextWorktreePath);
      } catch (error) {
        setOptimisticBranch(previousBranch);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Failed to switch ref.",
            description: toBranchActionErrorMessage(error),
          }),
        );
      }
    });
  };

  const runCreateRef = (name: string) => {
    const api = readEnvironmentApi(environmentId);
    if (!api || !branchCwd) return;

    runBranchAction(async () => {
      const previousBranch = resolvedActiveBranch;
      setOptimisticBranch(name);
      try {
        const createBranchResult = await api.vcs.createRef({
          cwd: branchCwd,
          refName: name,
          switchRef: true,
        });
        setOptimisticBranch(createBranchResult.refName);
        setThreadBranch(createBranchResult.refName, activeWorktreePath);
      } catch (error) {
        setOptimisticBranch(previousBranch);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Failed to create and switch ref.",
            description: toBranchActionErrorMessage(error),
          }),
        );
      }
    });
  };

  const selectBranch = (refName: VcsRef) => {
    const api = readEnvironmentApi(environmentId);
    if (!api || !branchCwd || !activeProjectCwd || isBranchActionPending) return;

    if (isSelectingWorktreeBase) {
      setThreadBranch(refName.name, null);
      setIsBranchMenuOpen(false);
      onComposerFocusRequest?.();
      return;
    }

    const selectionTarget = resolveBranchSelectionTarget({
      activeProjectCwd,
      activeWorktreePath,
      refName,
    });

    if (selectionTarget.reuseExistingWorktree) {
      setThreadBranch(refName.name, selectionTarget.nextWorktreePath);
      announceQueuedCheckoutSwitch(selectionTarget.nextWorktreePath);
      setIsBranchMenuOpen(false);
      onComposerFocusRequest?.();
      return;
    }

    if (branchStatusQuery.data?.hasWorkingTreeChanges) {
      setIsBranchMenuOpen(false);
      onComposerFocusRequest?.();
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Cannot switch branches with changes",
          description: "Commit or stash your working tree changes before switching branches.",
        }),
      );
      return;
    }

    setIsBranchMenuOpen(false);
    onComposerFocusRequest?.();

    // A real `git switch` in the checkout the agent is working in swaps files
    // underneath it mid-turn. Confirm first; when idle it stays immediate.
    // Picking a ref that resolves to a different checkout never lands here —
    // that is a checkout switch, and those are now free of side effects.
    if (hasActiveThreadTurn(serverSession)) {
      setPendingWorkingTreeAction({ kind: "switch", refName });
      return;
    }

    runSwitchRef(refName, selectionTarget.checkoutCwd, selectionTarget.nextWorktreePath);
  };

  const createRef = (rawName: string) => {
    const name = rawName.trim();
    const api = readEnvironmentApi(environmentId);
    if (!api || !branchCwd || !name || isBranchActionPending) return;

    setIsBranchMenuOpen(false);
    onComposerFocusRequest?.();

    if (hasActiveThreadTurn(serverSession)) {
      setPendingWorkingTreeAction({ kind: "create", name });
      return;
    }

    runCreateRef(name);
  };

  const confirmPendingWorkingTreeAction = () => {
    const pending = pendingWorkingTreeAction;
    setPendingWorkingTreeAction(null);
    if (!pending) return;
    if (pending.kind === "create") {
      runCreateRef(pending.name);
      return;
    }
    if (!activeProjectCwd) return;
    const selectionTarget = resolveBranchSelectionTarget({
      activeProjectCwd,
      activeWorktreePath,
      refName: pending.refName,
    });
    runSwitchRef(pending.refName, selectionTarget.checkoutCwd, selectionTarget.nextWorktreePath);
  };

  useEffect(() => {
    if (effectiveEnvMode !== "worktree" || activeWorktreePath || activeThreadBranch) {
      return;
    }
    const baseBranch = resolveDefaultWorktreeBaseBranch({ refs, currentGitBranch });
    if (!baseBranch) {
      return;
    }
    setThreadBranch(baseBranch, null);
  }, [
    activeThreadBranch,
    activeWorktreePath,
    currentGitBranch,
    effectiveEnvMode,
    refs,
    setThreadBranch,
  ]);

  // ---------------------------------------------------------------------------
  // Combobox / list plumbing
  // ---------------------------------------------------------------------------
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsBranchMenuOpen(open);
      if (!open) {
        setBranchQuery("");
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: gitQueryKeys.refs(environmentId, branchCwd),
      });
    },
    [branchCwd, environmentId, queryClient],
  );

  const branchListScrollElementRef = useRef<HTMLDivElement | null>(null);
  const maybeFetchNextBranchPage = useCallback(() => {
    if (!isBranchMenuOpen || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const scrollElement = branchListScrollElementRef.current;
    if (!scrollElement) {
      return;
    }

    const distanceFromBottom =
      scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
    if (distanceFromBottom > 96) {
      return;
    }

    void fetchNextPage().catch(() => undefined);
  }, [fetchNextPage, hasNextPage, isBranchMenuOpen, isFetchingNextPage]);
  const branchListRef = useRef<LegendListRef | null>(null);
  const setBranchListRef = useCallback((element: HTMLDivElement | null) => {
    branchListScrollElementRef.current = (element?.parentElement as HTMLDivElement | null) ?? null;
  }, []);

  useEffect(() => {
    if (!isBranchMenuOpen) {
      return;
    }

    if (shouldVirtualizeBranchList) {
      branchListRef.current?.scrollToOffset?.({ offset: 0, animated: false });
    } else {
      branchListScrollElementRef.current?.scrollTo({ top: 0 });
    }
  }, [deferredTrimmedBranchQuery, isBranchMenuOpen, shouldVirtualizeBranchList]);

  useEffect(() => {
    const scrollElement = branchListScrollElementRef.current;
    if (!scrollElement || !isBranchMenuOpen) {
      return;
    }

    const handleScroll = () => {
      maybeFetchNextBranchPage();
    };

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [isBranchMenuOpen, maybeFetchNextBranchPage]);

  useEffect(() => {
    if (shouldVirtualizeBranchList) return;
    maybeFetchNextBranchPage();
  }, [refs.length, maybeFetchNextBranchPage, shouldVirtualizeBranchList]);

  const triggerLabel = annotateMissingCheckoutLabel(
    presentation === "sentence"
      ? (resolvedActiveBranch ?? "pick a branch")
      : getBranchTriggerLabel({
          activeWorktreePath,
          effectiveEnvMode,
          resolvedActiveBranch,
        }),
    selectedCheckoutMissing,
  );
  const triggerDisabled = (isBranchesSearchPending && refs.length === 0) || isBranchActionPending;

  function renderPickerItem(itemValue: string, index: number) {
    if (checkoutPullRequestItemValue && itemValue === checkoutPullRequestItemValue) {
      return (
        <ComboboxItem
          hideIndicator
          key={itemValue}
          index={index}
          value={itemValue}
          onClick={() => {
            if (!prReference || !onCheckoutPullRequestRequest) {
              return;
            }
            setIsBranchMenuOpen(false);
            setBranchQuery("");
            onComposerFocusRequest?.();
            onCheckoutPullRequestRequest(prReference);
          }}
        >
          <div className="flex min-w-0 items-center gap-2 py-1">
            <SourceControlIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="flex min-w-0 flex-col items-start">
              <span className="truncate font-medium">
                Checkout {sourceControlPresentation.terminology.singular}
              </span>
              <span className="truncate text-muted-foreground text-xs">{prReference}</span>
            </span>
          </div>
        </ComboboxItem>
      );
    }
    if (createBranchItemValue && itemValue === createBranchItemValue) {
      return (
        <ComboboxItem
          hideIndicator
          key={itemValue}
          index={index}
          value={itemValue}
          onClick={() => createRef(trimmedBranchQuery)}
        >
          <span className="truncate">Create new ref &quot;{trimmedBranchQuery}&quot;</span>
        </ComboboxItem>
      );
    }

    const refName = branchByName.get(itemValue);
    if (!refName) return null;

    const badge = getVcsRefBadge(refName, activeProjectCwd);
    return (
      <ComboboxItem
        hideIndicator
        key={itemValue}
        index={index}
        value={itemValue}
        onClick={() => selectBranch(refName)}
      >
        <div className="flex w-full items-center justify-between gap-2">
          <span className="truncate">{itemValue}</span>
          {badge && <span className="shrink-0 text-[10px] text-muted-foreground/45">{badge}</span>}
        </div>
      </ComboboxItem>
    );
  }

  return (
    <>
      <AlertDialog
        open={pendingWorkingTreeAction !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingWorkingTreeAction(null);
          }
        }}
      >
        <AlertDialogPopup className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch branch while the agent is working?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingWorkingTreeAction?.kind === "create"
                ? `Creating "${pendingWorkingTreeAction.name}" switches this checkout to it.`
                : pendingWorkingTreeAction
                  ? `Switching to "${pendingWorkingTreeAction.refName.name}" changes the files in this checkout.`
                  : ""}{" "}
              The agent is running a turn here and will see the new files mid-task.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" size="sm" />}>
              Cancel
            </AlertDialogClose>
            <Button variant="destructive" size="sm" onClick={confirmPendingWorkingTreeAction}>
              Switch anyway
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
      <Combobox
        items={branchPickerItems}
        filteredItems={filteredBranchPickerItems}
        autoHighlight
        virtualized={shouldVirtualizeBranchList}
        onItemHighlighted={(_value, eventDetails) => {
          if (!isBranchMenuOpen || eventDetails.index < 0 || eventDetails.reason !== "keyboard") {
            return;
          }
          branchListRef.current?.scrollIndexIntoView?.({
            index: eventDetails.index,
            animated: false,
          });
        }}
        onOpenChange={handleOpenChange}
        open={isBranchMenuOpen}
        value={resolvedActiveBranch}
      >
        {presentation === "sentence" ? (
          <ComboboxTrigger
            className={cn(inlinePickerTriggerClassName, className)}
            disabled={triggerDisabled}
          >
            <span className="min-w-0 max-w-[240px] truncate">{triggerLabel}</span>
          </ComboboxTrigger>
        ) : (
          <ComboboxTrigger
            render={<Button variant="ghost" size="xs" />}
            className={cn("min-w-0 text-muted-foreground/70 hover:text-foreground/80", className)}
            disabled={triggerDisabled}
          >
            <span className="min-w-0 max-w-[240px] truncate">{triggerLabel}</span>
            <ChevronDownIcon className="shrink-0" />
          </ComboboxTrigger>
        )}
        <ComboboxPopup
          align={presentation === "sentence" ? "start" : "end"}
          side={presentation === "sentence" ? "bottom" : "top"}
          className="w-80"
        >
          <div className="border-b p-1">
            <ComboboxInput
              className="[&_input]:font-sans rounded-md"
              inputClassName="ring-0"
              placeholder="Search refs..."
              showTrigger={false}
              size="sm"
              value={branchQuery}
              onChange={(event) => setBranchQuery(event.target.value)}
            />
          </div>
          <ComboboxEmpty>No refs found.</ComboboxEmpty>

          {shouldVirtualizeBranchList ? (
            <ComboboxListVirtualized>
              <LegendList<string>
                ref={branchListRef}
                data={filteredBranchPickerItems}
                keyExtractor={(item) => item}
                renderItem={({ item, index }) => renderPickerItem(item, index)}
                estimatedItemSize={28}
                drawDistance={336}
                onEndReached={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                    void fetchNextPage().catch(() => undefined);
                  }
                }}
                style={{ maxHeight: "14rem" }}
              />
            </ComboboxListVirtualized>
          ) : (
            <ComboboxList ref={setBranchListRef} className="max-h-56">
              {filteredBranchPickerItems.map((itemValue, index) =>
                renderPickerItem(itemValue, index),
              )}
            </ComboboxList>
          )}
          {branchStatusText ? <ComboboxStatus>{branchStatusText}</ComboboxStatus> : null}
        </ComboboxPopup>
      </Combobox>
    </>
  );
}
